/**
 *
 * "Logged in user details page"
 *
 * @file   AccountPage.js
 * @author Lateral
 * @since  2023
 */
import React, { useState, useEffect } from 'react'
import Box from '@mui/material/Box'
import { useCurrentUser, observeRoles, observeCustomers } from 'hooks'
import { Typography, Stack, Button, Chip, CircularProgress, Avatar, Divider } from '@mui/material'
import { Email, Business, AdminPanelSettings, Shield } from '@mui/icons-material'
import MFASetup from './components/mfa/MFASetup'
import { getMFAStatus, disableMFA } from 'service/mfa'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { accent } from 'mainTheme'
import { useLocation } from 'react-router-dom'

const InfoRow = ({ icon, label, children }) => (
  <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ py: 2 }}>
    <Box sx={{ color: accent, mt: 0.25, flexShrink: 0 }}>{icon}</Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
          opacity: 0.6,
          mb: 0.5,
          fontSize: '11px',
          letterSpacing: '0.08em',
          textTransform: 'uppercase'
        }}>
        {label}
      </Typography>
      {children}
    </Box>
  </Stack>
)

const AccountPage = () => {
  /**
   * Generates a page showing details of logged in user
   *
   * @const
   *
   * @returns {object} - React app page
   */
  const { currentUser } = useCurrentUser()
  const [isLoading] = observeRoles()
  const customers = observeCustomers()
  const companyNames = customers.filter((c) => currentUser.customerIds.includes(c.id)).map((c) => c.Name)

  const [mfaDialogOpen, setMfaDialogOpen] = useState(false)
  const queryClient = useQueryClient()
  const location = useLocation()

  useEffect(() => {
    if (location.state?.openMFA) {
      setMfaDialogOpen(true)
    }
  }, [location.state?.openMFA])

  const { data: mfaStatus, isLoading: mfaLoading } = useQuery({
    queryKey: ['mfaStatus'],
    queryFn: getMFAStatus,
    select: (data) => data.totpEnabled
  })

  const disableMutation = useMutation({
    mutationFn: disableMFA,
    onSuccess: () => {
      queryClient.setQueryData(['mfaStatus'], (prev) => ({ ...prev, totpEnabled: false }))
      // Prevent the MFA prompt from re-appearing in this session after explicit disable
      sessionStorage.setItem('mfa_prompt_dismissed', '1')
    }
  })

  const handleDisableMFA = () => disableMutation.mutate()

  if (isLoading) {
    return 'Loading...'
  }

  const email = currentUser?.attributes?.email ?? ''
  const initials = email ? email.slice(0, 2).toUpperCase() : '?'
  const roles = currentUser?.getRoles()?.map((x) => x.Name) ?? []

  return (
    <Box sx={{ pt: 1 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Account Overview
      </Typography>

      <Box sx={{ maxWidth: 520 }}>
        {/* Profile header */}
        <Stack direction="row" spacing={2.5} alignItems="center" sx={{ mb: 3 }}>
          <Avatar
            sx={{
              width: 56,
              height: 56,
              bgcolor: accent,
              color: 'background.default',
              fontWeight: 600,
              fontSize: '18px'
            }}>
            {initials}
          </Avatar>
          <Box>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {email || '--'}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.5, mt: 0.25 }}>
              {roles.length > 0 ? roles.join(', ') : 'No roles assigned'}
            </Typography>
          </Box>
        </Stack>

        {/* Details card */}
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'rgba(255,255,255,0.08)',
            borderRadius: 2,
            px: 3,
            bgcolor: 'rgba(255,255,255,0.03)'
          }}>
          <InfoRow icon={<Email fontSize="small" />} label="Email">
            <Typography variant="body2">{email || '--'}</Typography>
          </InfoRow>

          <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />

          <InfoRow icon={<Business fontSize="small" />} label="Companies">
            {companyNames.length > 0 ? (
              <Stack direction="row" flexWrap="wrap" gap={0.75}>
                {companyNames.map((name) => (
                  <Chip
                    key={name}
                    label={name}
                    size="small"
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.07)',
                      color: 'text.primary',
                      borderRadius: '6px',
                      height: 24,
                      fontSize: '12px'
                    }}
                  />
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" sx={{ opacity: 0.5 }}>
                --
              </Typography>
            )}
          </InfoRow>

          <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />

          <InfoRow icon={<AdminPanelSettings fontSize="small" />} label="Roles">
            {roles.length > 0 ? (
              <Stack direction="row" flexWrap="wrap" gap={0.75}>
                {roles.map((role) => (
                  <Chip
                    key={role}
                    label={role}
                    size="small"
                    sx={{
                      bgcolor: `${accent}22`,
                      color: accent,
                      borderRadius: '6px',
                      height: 24,
                      fontSize: '12px',
                      border: `1px solid ${accent}44`
                    }}
                  />
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" sx={{ opacity: 0.5 }}>
                --
              </Typography>
            )}
          </InfoRow>

          <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />

          <InfoRow icon={<Shield fontSize="small" />} label="Two-Factor Authentication">
            {mfaLoading ? (
              <CircularProgress size={18} sx={{ mt: 0.25 }} />
            ) : (
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Chip
                  label={mfaStatus ? 'Enabled' : 'Disabled'}
                  size="small"
                  sx={{
                    bgcolor: mfaStatus ? 'rgba(46,160,67,0.15)' : 'rgba(255,255,255,0.07)',
                    color: mfaStatus ? '#3fb950' : 'rgba(255,255,255,0.45)',
                    border: mfaStatus ? '1px solid rgba(46,160,67,0.4)' : '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '6px',
                    height: 22,
                    fontSize: '11px'
                  }}
                />
                {mfaStatus ? (
                  <Button
                    size="small"
                    color="error"
                    variant="outlined"
                    onClick={handleDisableMFA}
                    disabled={disableMutation.isLoading}
                    sx={{ height: 28, fontSize: '12px', minWidth: 80 }}>
                    {disableMutation.isLoading ? <CircularProgress size={14} /> : 'Disable'}
                  </Button>
                ) : (
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => setMfaDialogOpen(true)}
                    sx={{ height: 28, fontSize: '12px', minWidth: 80 }}>
                    Enable
                  </Button>
                )}
              </Stack>
            )}
          </InfoRow>
        </Box>
      </Box>

      <MFASetup
        open={mfaDialogOpen}
        onClose={() => setMfaDialogOpen(false)}
        onEnabled={() => queryClient.invalidateQueries(['mfaStatus'])}
      />
    </Box>
  )
}
export default AccountPage
